import { confirm } from "@inquirer/prompts";
import { Command, Option } from "clipanion";
import { M8tCommand } from "../../lib/m8t-command.js";
import type {
  CleanupOrphanedResponse,
  DeleteBindingResponse,
  GetBindingResponse,
  ListBindingsResponse,
} from "@m8t-stack/api-contract";
import { apiCall } from "../../lib/api-client.js";
import { LocalCliError } from "../../lib/errors.js";
import { resolveGatewayContext } from "../../lib/gateway-context.js";
import {
  colors,
  renderJson,
  resolveOutputMode,
} from "../../lib/output.js";

export class BindCleanupCommand extends M8tCommand {
  static paths = [["bind", "cleanup"]];
  static usage = Command.Usage({
    description: "Remove orphaned binding(s) — janitor counterpart to 'bind remove'.",
    details:
      "Refuses to clean up active bindings (use 'bind remove' instead). With --all-orphaned, bulk-cleans every orphaned binding; per-binding partial failures are collected and reported, not aborted.",
    examples: [
      ["Cleanup one orphaned binding", "$0 bind cleanup cpo-tg"],
      ["Cleanup all orphaned bindings", "$0 bind cleanup --all-orphaned"],
      ["Skip confirms in scripts", "$0 bind cleanup --all-orphaned --yes"],
    ],
  });

  bindingId = Option.String({ required: false });
  allOrphaned = Option.Boolean("--all-orphaned", false);
  yes = Option.Boolean("--yes", false);
  output = Option.String("--output");
  subscription = Option.String("--subscription", {
    description: "Azure subscription ID to discover gateway in (defaults to active az subscription).",
  });
  resourceGroup = Option.String("--resource-group", {
    description: "m8t-stack resource group to disambiguate the gateway (multi-deployment subscriptions).",
  });

  async executeCommand(): Promise<number> {
    if (this.bindingId && this.allOrphaned) {
      throw new LocalCliError({
        code: "USAGE",
        message:
          "specify EITHER a binding id OR --all-orphaned, not both.",
        hint: "Run 'm8t bind cleanup cpo-tg' to clean one, or 'm8t bind cleanup --all-orphaned' for bulk.",
      });
    }
    if (!this.bindingId && !this.allOrphaned) {
      throw new LocalCliError({
        code: "USAGE",
        message: "specify a binding id or pass --all-orphaned.",
        hint: "Run 'm8t bind cleanup --help' for examples.",
      });
    }

    const mode = resolveOutputMode(
      this.output as "pretty" | "json" | "auto" | undefined,
      this.context.stdout as NodeJS.WriteStream,
    );
    const interactive = (this.context.stdout as NodeJS.WriteStream).isTTY === true;
    const ctx = await resolveGatewayContext({ interactive, subscriptionId: this.subscription, resourceGroup: this.resourceGroup });

    if (this.allOrphaned) {
      return await this.runBulkCleanup(ctx, mode, interactive);
    }
    if (typeof this.bindingId !== "string") {
      throw new LocalCliError({ code: "USAGE", message: "<binding-id> is required when not using --all-orphaned" });
    }
    return await this.runSingleCleanup(ctx, mode, interactive, this.bindingId);
  }

  private async runSingleCleanup(
    ctx: { gatewayUrl: string; gatewayClientId: string },
    mode: "pretty" | "json",
    interactive: boolean,
    bindingId: string,
  ): Promise<number> {
    // Pre-check: ensure status === orphaned before any destructive call.
    const show = await apiCall<GetBindingResponse>(
      { gatewayUrl: ctx.gatewayUrl, gatewayClientId: ctx.gatewayClientId },
      { method: "GET", path: `/api/bindings/${encodeURIComponent(bindingId)}` },
      (msg) => this.context.stderr.write(msg + "\n"),
    );

    if (show.binding.status !== "orphaned") {
      this.context.stderr.write(
        colors.error("error:") +
          ` binding '${bindingId}' is active. Use 'm8t bind remove ${bindingId}' instead — cleanup is for orphaned bindings only.\n`,
      );
      return 1;
    }

    if (!this.yes && interactive) {
      const ok = await confirm({
        message: `Cleanup orphaned binding '${bindingId}'? This deletes its KV secret and any conversation threads.`,
        default: false,
      });
      if (!ok) {
        this.context.stdout.write("cancelled — nothing removed.\n");
        return 2;
      }
    }

    const data = await apiCall<DeleteBindingResponse>(
      { gatewayUrl: ctx.gatewayUrl, gatewayClientId: ctx.gatewayClientId },
      { method: "DELETE", path: `/api/bindings/${encodeURIComponent(bindingId)}?cleanup=true` },
      (msg) => this.context.stderr.write(msg + "\n"),
    );

    if (mode === "json") {
      this.context.stdout.write(renderJson(data) + "\n");
      return 0;
    }

    this.context.stdout.write(
      `${colors.success("✓")} cleaned orphaned binding ${colors.field(data.deleted.bindingId)}.\n`,
    );
    return 0;
  }

  private async runBulkCleanup(
    ctx: { gatewayUrl: string; gatewayClientId: string },
    mode: "pretty" | "json",
    interactive: boolean,
  ): Promise<number> {
    // List orphans first for the prompt count.
    const list = await apiCall<ListBindingsResponse>(
      { gatewayUrl: ctx.gatewayUrl, gatewayClientId: ctx.gatewayClientId },
      { method: "GET", path: "/api/bindings?status=orphaned" },
      (msg) => this.context.stderr.write(msg + "\n"),
    );

    if (list.bindings.length === 0) {
      this.context.stdout.write("no orphaned bindings to clean up.\n");
      return 0;
    }

    if (!this.yes && interactive) {
      const ok = await confirm({
        message: `${list.bindings.length.toString()} orphaned binding(s) found — clean up all of them?`,
        default: false,
      });
      if (!ok) {
        this.context.stdout.write("cancelled — nothing removed.\n");
        return 2;
      }
    }

    const data = await apiCall<CleanupOrphanedResponse>(
      { gatewayUrl: ctx.gatewayUrl, gatewayClientId: ctx.gatewayClientId },
      { method: "POST", path: "/api/bindings/cleanup-orphaned", body: {} },
      (msg) => this.context.stderr.write(msg + "\n"),
    );

    if (mode === "json") {
      this.context.stdout.write(renderJson(data) + "\n");
      return data.failed.length > 0 ? 1 : 0;
    }

    if (data.failed.length === 0) {
      this.context.stdout.write(
        `${colors.success("✓")} cleaned ${data.cleaned.length.toString()} binding(s). 0 failed.\n`,
      );
      return 0;
    }

    // Mixed or all-failed — render per-binding failures inline.
    this.context.stderr.write(
      colors.error("⚠") +
        ` cleaned ${data.cleaned.length.toString()} binding(s); ${data.failed.length.toString()} failed:\n`,
    );
    for (const fail of data.failed) {
      this.context.stderr.write(`  - ${fail.bindingId} (${fail.channel}): ${fail.originalMessage}\n`);
    }
    this.context.stderr.write(
      colors.hint("  hint:") +
        " re-run 'm8t bind cleanup --all-orphaned' to retry failed ones; each cascade is idempotent.\n",
    );
    return 1;
  }
}
